import type { ComponentType } from 'react'
import { navigate } from '../lib/router'
import { useStore } from '../store'
import { SECTIONS, isHidden, type SectionId } from './sections'

/**
 * Fixed to the bottom, so sections are switched here rather than by going back to the
 * dashboard each time.
 *
 * Which tabs appear comes from the section list and the user's settings: hiding To-do
 * takes it out of here too, and the remaining tabs simply get wider.
 */
const ICONS: Record<SectionId, ComponentType> = {
  budget: WalletIcon,
  nutrition: LeafIcon,
  health: HeartIcon,
  todo: CheckIcon,
  savings: WalletIcon,
  cookbook: DotsIcon,
  exercise: DotsIcon,
  game: DotsIcon,
  emergency: DotsIcon,
}

export function BottomNav({ route }: { route: string }) {
  const { snapshot } = useStore()
  const hidden = snapshot?.app.hiddenSections ?? []

  const tabs = SECTIONS.filter((s) => s.inNav && !isHidden(hidden, s.id))
  // Everything reached from the drawer keeps the Other tab lit, including the routes of
  // sections that were moved out of the bar.
  const drawerRoutes = ['/other', '/settings', ...SECTIONS.filter((s) => !s.inNav).map((s) => s.route)]

  const items = [
    { key: 'home', route: '/', label: 'Dashboard', match: (r: string) => r === '/', icon: HomeIcon },
    ...tabs.map((s) => ({
      key: s.id,
      route: s.route,
      label: s.label,
      match: (r: string) => r.startsWith(s.route),
      icon: ICONS[s.id],
    })),
    {
      key: 'other',
      route: '/other',
      label: 'Other',
      match: (r: string) => drawerRoutes.some((d) => r.startsWith(d)),
      icon: DotsIcon,
    },
  ]

  return (
    <nav className="bottomnav">
      {items.map((item) => {
        const current = item.match(route)
        const Icon = item.icon
        return (
          <button
            key={item.key}
            type="button"
            className="navitem"
            aria-current={current ? 'page' : undefined}
            onClick={() => navigate(item.route)}
          >
            <span className="pill">
              <Icon />
            </span>
            {item.label}
          </button>
        )
      })}
    </nav>
  )
}

function HomeIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  )
}

function WalletIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21 7.5V6a2 2 0 0 0-2-2H5.5A2.5 2.5 0 0 0 3 6.5v11A2.5 2.5 0 0 0 5.5 20H19a2 2 0 0 0 2-2v-1.5h-8a2.5 2.5 0 0 1 0-5h8Zm-8 1.5h9v4h-9a2 2 0 0 1 0-4Z" />
    </svg>
  )
}

function LeafIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8.1 13.34 3.91 9.16a5 5 0 0 1 7.07-7.07l1.06 1.06-2.83 2.83 1.42 1.41 2.82-2.83 1.42 1.42-2.83 2.83 1.41 1.41 2.83-2.83 1.06 1.06a5 5 0 0 1-7.07 7.07l-.19-.19-7.1 7.1-1.41-1.41 7.1-7.1z" />
    </svg>
  )
}

function HeartIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 21.35 10.55 20C5.4 15.36 2 12.27 2 8.5 2 5.41 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.41 22 8.5c0 3.77-3.4 6.86-8.55 11.53z" />
    </svg>
  )
}

function DotsIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-2 15-5-5 1.4-1.4L10 14.2l7.6-7.6L19 8l-9 9Z" />
    </svg>
  )
}
